import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../../api/client'
import type { Role } from '../../api/types'
import { MIN_PASSWORD_LENGTH } from '../PasswordForm'

/**
 * An account made outright, with a password the admin chooses, for someone who can't take an invite link. The same
 * email and password sign in to StoreKeeper; an email that already has a StoreKeeper account keeps its password.
 */
export function CreateUserPanel() {
	const client = useQueryClient()
	const [email, setEmail] = useState('')
	const [name, setName] = useState('')
	const [password, setPassword] = useState('')
	const [role, setRole] = useState<Role>('viewer')
	const [created, setCreated] = useState<{ email: string; existingAccount: boolean } | null>(null)
	const create = useMutation({
		mutationFn: () =>
			api.post<{ email: string; existingAccount: boolean }>('/api/admin/users', { email, name, role, password }),
		onSuccess: (user) => {
			setCreated({ email: user.email, existingAccount: user.existingAccount })
			setEmail('')
			setName('')
			setPassword('')
			client.invalidateQueries({ queryKey: ['admin-users'] })
		},
	})
	function submit(e: FormEvent) {
		e.preventDefault()
		setCreated(null)
		create.mutate()
	}
	return (
		<section className="card stack">
			<h2 style={{ margin: 0 }}>Create user</h2>
			<form className="row" onSubmit={submit}>
				<input
					type="email"
					required
					placeholder="name@example.com"
					aria-label="New user email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					style={{ flex: '1 1 220px' }}
				/>
				<input
					required
					placeholder="Name"
					aria-label="New user name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					style={{ flex: '1 1 160px' }}
				/>
				<input
					type="password"
					required
					minLength={MIN_PASSWORD_LENGTH}
					autoComplete="new-password"
					placeholder={`Password (${MIN_PASSWORD_LENGTH}+ characters)`}
					aria-label="New user password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					style={{ flex: '1 1 200px' }}
				/>
				<select aria-label="New user role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
					<option value="viewer">Viewer</option>
					<option value="admin">Admin</option>
				</select>
				<button className="primary" type="submit" disabled={create.isPending}>
					Create user
				</button>
			</form>
			{create.isError && <p className="error small">{create.error.message}</p>}
			{created && (
				<p className="small">
					{created.existingAccount
						? `${created.email} already had a StoreKeeper account, so it now has website access too. Their own password is unchanged; the one you entered wasn’t used.`
						: `Created ${created.email}. They can sign in now, on the website and in StoreKeeper, with the password you set.`}
				</p>
			)}
		</section>
	)
}
